import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import ThemeTag from './ThemeTag';
import { CHANAKYA_COLORS } from './chanakyaColors';

const Card = styled.article`
  max-width: 640px;
  margin: 0 auto;
  padding: ${p => p.theme.spacing[5]}px;
  background: ${CHANAKYA_COLORS.parchment};
  border: 1px solid ${CHANAKYA_COLORS.cellBorder};
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(48,63,159,0.08);
  text-align: center;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: ${p => p.theme.spacing[4]}px;
  }
`;

const Motif = styled.div`
  font-size: 36px;
  margin-bottom: ${p => p.theme.spacing[2]}px;
  line-height: 1;
`;

const Heading = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 22px;
  color: ${CHANAKYA_COLORS.primaryDark};
  margin: 0 0 ${p => p.theme.spacing[3]}px;
`;

const TagRow = styled.div`
  margin-bottom: ${p => p.theme.spacing[3]}px;
`;

const AphorismNum = styled.div`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: ${p => p.theme.typography.sizes.md};
  color: ${CHANAKYA_COLORS.accentDark};
  font-weight: 600;
  margin-bottom: ${p => p.theme.spacing[3]}px;
  letter-spacing: 1px;
`;

const ShlokaDev = styled.p`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 19px;
  line-height: 1.8;
  color: #2C1810;
  margin: ${p => p.theme.spacing[4]}px 0 ${p => p.theme.spacing[2]}px;
`;

const TranslationTa = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${CHANAKYA_COLORS.textSecondary};
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const InterpHeading = styled.h3`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${CHANAKYA_COLORS.primaryDark};
  margin: ${p => p.theme.spacing[5]}px 0 ${p => p.theme.spacing[2]}px;
`;

const InterpPrimary = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: #2C1810;
  margin: 0 0 ${p => p.theme.spacing[3]}px;
`;

const InterpSecondary = styled.p`
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.colors.text.secondary};
  font-style: italic;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing[3]}px;
  justify-content: center;
  margin-top: ${p => p.theme.spacing[5]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    flex-direction: column;
  }
`;

const ShareBtn = styled.button`
  padding: 12px 32px;
  background: linear-gradient(135deg, ${CHANAKYA_COLORS.primary}, ${CHANAKYA_COLORS.accent});
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const ReturnBtn = styled.button`
  padding: 12px 32px;
  background: none;
  border: 1px solid ${CHANAKYA_COLORS.primary};
  color: ${CHANAKYA_COLORS.primaryDark};
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const ChanakyaResultCard = ({ aphorism, onShare, onReturn, lang }) => {
  const t = useLocale(lang, 'chanakyaNiti');
  const primary = lang === 'ta' ? aphorism.interpretation_ta : aphorism.interpretation_en;
  const secondary = lang === 'ta' ? aphorism.interpretation_en : aphorism.interpretation_ta;
  const aphNum = (t.aphorismNumber || 'Aphorism {n}').replace('{n}', aphorism.cell);

  return (
    <Card>
      <Motif aria-hidden="true">📜</Motif>
      <Heading>{t.resultHeading || "Chanakya's Counsel"}</Heading>

      <TagRow>
        <ThemeTag theme={aphorism.theme} cell={aphorism.cell} lang={lang} />
      </TagRow>

      <AphorismNum>{aphNum}</AphorismNum>

      <ShlokaDev>{aphorism.shloka_sa}</ShlokaDev>
      <TranslationTa>{aphorism.translation_ta}</TranslationTa>

      <InterpHeading>{t.interpretationHeading || 'Meaning'}</InterpHeading>
      <InterpPrimary>{primary}</InterpPrimary>
      <InterpSecondary>{secondary}</InterpSecondary>

      <Actions>
        <ShareBtn onClick={onShare}>{t.shareButton || 'Share'}</ShareBtn>
        <ReturnBtn onClick={onReturn}>{t.returnButton || 'Return'}</ReturnBtn>
      </Actions>
    </Card>
  );
};

export default ChanakyaResultCard;
